import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { MapPin, Building2, Briefcase, IndianRupee } from "lucide-react";
import { Button } from "@/components/ui/button";
import PublicHeader from "@/components/PublicHeader";
import Seo from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TYPE_MAP = { "full-time": "FULL_TIME", "part-time": "PART_TIME", locum: "CONTRACTOR", internship: "INTERN", residency: "OTHER" };

const jobLd = (j) => ({
  "@context": "https://schema.org", "@type": "JobPosting",
  title: j.title, description: j.description, datePosted: j.created_at,
  validThrough: new Date(new Date(j.created_at).getTime() + 60 * 864e5).toISOString(),
  employmentType: TYPE_MAP[j.employment_type] || "OTHER",
  hiringOrganization: { "@type": "Organization", name: j.hospital },
  jobLocation: { "@type": "Place", address: { "@type": "PostalAddress", addressLocality: j.location, addressCountry: "IN" } },
  directApply: false,
});

export default function PublicJob() {
  const { id } = useParams();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { axios.get(`${API}/public/jobs/${id}`).then((r) => setJob(r.data)).catch(() => setErr(true)); }, [id]);

  return (
    <div className="min-h-screen bg-[color:var(--ayur-cream)]" data-testid="public-job-page">
      {job && <Seo title={`${job.title} — ${job.hospital}, ${job.location}`} description={job.description.slice(0, 155)} path={`/jobs/${id}`} jsonLd={jobLd(job)} />}
      {err && <Seo title="Job not found" path={`/jobs/${id}`} noindex />}
      <PublicHeader appPath="/app/jobs" />
      <main className="max-w-3xl mx-auto px-6 py-10">
        <Link to="/jobs" className="text-xs font-mono-brand text-[color:var(--ayur-mute)] link-underline" data-testid="public-job-back">← All Ayurveda jobs</Link>
        {err && <div className="paper-card p-10 mt-4 text-center" data-testid="public-job-notfound">This job is no longer available.</div>}
        {!job && !err && <div className="paper-card p-10 mt-4 text-center text-[color:var(--ayur-mute)]">Loading…</div>}
        {job && (
          <article className="paper-card bg-white p-6 sm:p-8 mt-4" data-testid="public-job-card">
            <h1 className="font-serif-display text-3xl sm:text-4xl text-[color:var(--ayur-emerald)]" data-testid="public-job-title">{job.title}</h1>
            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-sm text-[color:var(--ayur-ink-2)]">
              <span className="inline-flex items-center gap-1"><Building2 className="w-4 h-4" />{job.hospital}</span>
              <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" />{job.location}</span>
              <span className="inline-flex items-center gap-1"><Briefcase className="w-4 h-4" />{job.employment_type}</span>
              {job.salary_range && <span className="inline-flex items-center gap-1"><IndianRupee className="w-4 h-4" />{job.salary_range}</span>}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {job.degree_required && <span className="pill">{job.degree_required}</span>}
              {job.specialty && <span className="pill">{job.specialty}</span>}
            </div>
            <div className="mt-6 whitespace-pre-wrap leading-relaxed text-[color:var(--ayur-ink)]">{job.description}</div>
            <div className="mt-8 p-5 rounded-xl bg-[color:var(--ayur-emerald)] text-white flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <span className="text-sm">{user ? "Apply from the VaidGram jobs board." : "Log in or join VaidGram (free for Ayurveda scholars) to see contact details and apply."}</span>
              <Link to={user ? "/app/jobs" : "/signup"}>
                <Button className="rounded-full bg-[color:var(--ayur-gold)] text-[color:var(--ayur-ink)] hover:bg-white" data-testid="public-job-apply-btn">{user ? "Apply in app" : "Join to apply"}</Button>
              </Link>
            </div>
          </article>
        )}
      </main>
    </div>
  );
}
